import { useEffect, useRef, useState, VFC } from 'react'
import { MasterDataStore } from 'src/api/masterData'
import { PersonaForm } from 'src/api/types/persona'
import {
  MasterData,
  MasterDataWithRegionalDivisionType,
} from 'src/models/MasterData'
import { Persona } from 'src/models/Persona/index'
import { LoginUser } from 'src/models/User'
import { _formalert, lightgray, orange, truewhite } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { flexCenter } from 'src/styles/mixins'
import styled from 'styled-components'
import { getPersonaCompanyCount } from '../../../api/persona'
import { PersonaBoxComponent } from './common'
import { Types } from './form'

type CompanyPersonasFormProps = {
  user: LoginUser
  masterData: MasterDataStore
  personaData: Persona
  errorMessages: string[]
  submit: (persona: PersonaForm) => void
}

export const CompanyPersonasForm: VFC<CompanyPersonasFormProps> = ({
  user,
  masterData,
  personaData,
  errorMessages,
  submit,
}) => {
  const handleSubmit = async () => {
    const personaForm: PersonaForm = {
      typeOfBusinesss: checkedBusinessTypes,
      employeeNumbers: checkedNumberOfEmployees,
      companyBuyNeedOptions: checkedNeeds,
      prefectures: checkedPrefectures,
      positions: checkedPositionNumberOptions,
      capitals: checkedCapitalNumberOptions,
      isNew: persona.isNew,
      noMessage: persona.noMessage,
      refuseMessage: persona.refuseMessage,
      isListedCompany: persona.isListedCompany,
      businessObjects: checkedBusinessObjects,
      foundedAts: checkedFoundedAts,
      adminAges: checkedAdminAges,
    }
    return await submit(personaForm)
  }

  const updatePersonaCompanyCount = async () => {
    setIsSearching(true)
    const personaForm: PersonaForm = {
      typeOfBusinesss: checkedBusinessTypes,
      employeeNumbers: checkedNumberOfEmployees,
      companyBuyNeedOptions: checkedNeeds,
      prefectures: checkedPrefectures,
      positions: checkedPositionNumberOptions,
      capitals: checkedCapitalNumberOptions,
      isNew: persona.isNew,
      noMessage: persona.noMessage,
      refuseMessage: persona.refuseMessage,
      isListedCompany: persona.isListedCompany,
      businessObjects: checkedBusinessObjects,
      foundedAts: checkedFoundedAts,
      adminAges: checkedAdminAges,
    }
    const res = await getPersonaCompanyCount(personaForm)
    if (res.data?.count != undefined) setSearchCount(res.data?.count)
    else setSearchCount(null)
    setIsSearching(false)
  }

  const [persona, setPersona] = useState<Persona>({
    id: personaData.id,
    personaAdminAgeIds: personaData.personaAdminAgeIds,
    personaBusinessObjectIds: personaData.personaBusinessObjectIds,
    personaEmployeeNumberIds: personaData.personaEmployeeNumberIds,
    personaFoundedAtIds: personaData.personaFoundedAtIds,
    personaPrefectureIds: personaData.personaPrefectureIds,
    personaTypeOfBusinessIds: personaData.personaTypeOfBusinessIds,
    personaCompanyBuyNeedOptionIds: personaData.personaCompanyBuyNeedOptionIds,
    personaSolutionIds: personaData.personaSolutionIds,
    personaCapitalIds: personaData.personaCapitalIds,
    personaPositionIds: personaData.personaPositionIds,
    personaRoleForAnalysisIds: [],
    refuseMessage: personaData.refuseMessage,
    noMessage: personaData.noMessage,
    isNew: personaData.isNew,
    isListedCompany: personaData.isListedCompany,
    freeWord: personaData.freeWord,
    minCreatedDate: null,
    maxCreatedDate: null,
    minLastSignInAt: null,
    maxLastSignInAt: null,
    personaAdminAges: personaData.personaAdminAges,
    personaCompanyBuyNeedOptions: personaData.personaCompanyBuyNeedOptions,
    personaBusinessObjects: personaData.personaBusinessObjects,
    personaSolutions: personaData.personaSolutions,
    personaEmployeeNumbers: personaData.personaEmployeeNumbers,
    personaFoundedAts: personaData.personaFoundedAts,
    personaPrefectures: personaData.personaPrefectures,
    personaTypeOfBusinesses: personaData.personaTypeOfBusinesses,
    personaCapitals: personaData.personaCapitals,
    personaPositions: personaData.personaPositions,
    personaRoleForAnalysis: [],
  })

  const [selectedType, setSelectedType] = useState<Types>('businessTypes')

  const [checkedBusinessTypes, setCheckedBusinessTypes] = useState<
    MasterData[]
  >(
    masterData.businessTypes!.filter(({ value }) =>
      persona.personaTypeOfBusinessIds.includes(Number(value)),
    ),
  )
  const [checkedNumberOfEmployees, setCheckedNumberOfEmployees] = useState<
    MasterData[]
  >(
    masterData.numberOfEmployees!.filter(({ value }) =>
      persona.personaEmployeeNumberIds.includes(Number(value)),
    ),
  )
  const [checkedNeeds, setCheckedNeeds] = useState<MasterData[]>(
    masterData.needs!.filter(({ value }) =>
      persona.personaCompanyBuyNeedOptionIds.includes(Number(value)),
    ),
  )
  const [checkedPrefectures, setCheckedPrefectures] = useState<
    MasterDataWithRegionalDivisionType[]
  >(
    masterData.prefectures!.filter(({ value }) =>
      persona.personaPrefectureIds.includes(Number(value)),
    ),
  )
  const [checkedPositionNumberOptions, setCheckedPositionNumberOptions] =
    useState<MasterData[]>(
      masterData.positionNumberOptions!.filter(({ value }) =>
        persona.personaPositionIds.includes(Number(value)),
      ),
    )
  const [checkedCapitalNumberOptions, setCheckedCapitalNumberOptions] =
    useState<MasterData[]>(
      masterData.capitalNumberOptions!.filter(({ value }) =>
        persona.personaCapitalIds.includes(Number(value)),
      ),
    )
  const [checkedBusinessObjects, setCheckedBusinessObject] = useState<
    MasterData[]
  >(
    masterData.businessObjects!.filter(({ value }) =>
      persona.personaBusinessObjectIds.includes(Number(value)),
    ),
  )
  const [checkedFoundedAts, setCheckedFoundedAts] = useState<MasterData[]>(
    masterData.foundedAts!.filter(({ value }) =>
      persona.personaFoundedAtIds.includes(Number(value)),
    ),
  )
  const [checkedAdminAges, setCheckedAdminAges] = useState<MasterData[]>(
    masterData.adminAges!.filter(({ value }) =>
      persona.personaAdminAgeIds.includes(Number(value)),
    ),
  )
  const [searchCount, setSearchCount] = useState<number | null>(null)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const isNew = persona.id == 0
  const [isDefault, setIsDefault] = useState<boolean>(isNew)
  const setOtherDetails = (checked: boolean) => {
    setPersona({
      ...persona,
      isNew: checked,
      noMessage: checked,
      refuseMessage: checked,
    })
  }
  const handleOtherDetailsChecked =
    persona.isNew && persona.noMessage && persona.refuseMessage

  const isFirstLoad = useRef(true)
  useEffect(() => {
    // 新規追加モーダルは企業数初期ロード無し、編集画面は初期ロード有り
    if (isFirstLoad.current && isNew) {
      isFirstLoad.current = false
      return
    }

    // 項目がデフォルト状態であれば決済者の数を表示しない
    const isDefaultCheck =
      checkedBusinessTypes.length === 0 &&
      checkedNumberOfEmployees.length === 0 &&
      checkedNeeds.length === 0 &&
      checkedPrefectures.length === 0 &&
      checkedPositionNumberOptions.length === 0 &&
      checkedCapitalNumberOptions.length === 0 &&
      persona.isNew === false &&
      persona.noMessage === true &&
      persona.refuseMessage === true &&
      persona.isListedCompany === null &&
      checkedFoundedAts.length === 0 &&
      checkedBusinessObjects.length === 0 &&
      checkedAdminAges.length === 0
    setIsDefault(isDefaultCheck)
    if (!isDefaultCheck) updatePersonaCompanyCount()
  }, [
    persona,
    checkedBusinessTypes,
    checkedNumberOfEmployees,
    checkedNeeds,
    checkedPrefectures,
    checkedPositionNumberOptions,
    checkedCapitalNumberOptions,
    checkedBusinessObjects,
    checkedFoundedAts,
    checkedAdminAges,
  ])

  return (
    <Layout>
      <TitleDiv>
        <h1>ペルソナ編集</h1>
      </TitleDiv>
      <FormPersonaMainContainer>
        <PersonaBoxComponent
          pageType="persona"
          role={user.plan.role}
          masterData={masterData}
          setSelectedType={setSelectedType}
          selectedType={selectedType}
          checkedBusinessTypes={checkedBusinessTypes}
          setCheckedBusinessTypes={setCheckedBusinessTypes}
          setCheckedSolutions={setCheckedBusinessTypes}
          checkedNumberOfEmployees={checkedNumberOfEmployees}
          setCheckedNumberOfEmployees={setCheckedNumberOfEmployees}
          checkedNeeds={checkedNeeds}
          setCheckedNeeds={setCheckedNeeds}
          checkedPrefectures={checkedPrefectures}
          setCheckedPrefectures={setCheckedPrefectures}
          checkedPositionNumberOptions={checkedPositionNumberOptions}
          setCheckedPositionNumberOptions={setCheckedPositionNumberOptions}
          checkedCapitalNumberOptions={checkedCapitalNumberOptions}
          setCheckedCapitalNumberOptions={setCheckedCapitalNumberOptions}
          handleOtherDetailsChecked={handleOtherDetailsChecked}
          setOtherDetails={setOtherDetails}
          persona={persona}
          setPersona={setPersona}
          checkedFoundedAts={checkedFoundedAts}
          setCheckedFoundedAts={setCheckedFoundedAts}
          checkedBusinessObjects={checkedBusinessObjects}
          checkedSolutions={[]}
          setCheckedBusinessObject={setCheckedBusinessObject}
          checkedAdminAges={checkedAdminAges}
          setCheckedAdminAges={setCheckedAdminAges}
        />
      </FormPersonaMainContainer>
      <SaveButtonDiv>
        <SaveButton type={'button'} onClick={handleSubmit}>
          保存する
        </SaveButton>
        <SaveButtonSideArea>
          <SaveButtonSideAreaInner>
            <PersonaCountDiv>
              選択したペルソナに一致する決裁者：
              {searchCount == null || isSearching || isDefault
                ? '-'
                : searchCount}
              人
            </PersonaCountDiv>
            {errorMessages.length > 0 && (
              <ErrorMessages>
                {errorMessages.map((errorMessage, index) => (
                  <ErrorMessage key={index}>{errorMessage}</ErrorMessage>
                ))}
              </ErrorMessages>
            )}
          </SaveButtonSideAreaInner>
        </SaveButtonSideArea>
      </SaveButtonDiv>
    </Layout>
  )
}

const Layout = styled.div`
  border-radius: 16px;
  width: 928px;
  max-width: min(80vw, 928px);

  line-height: 1.4;
`

const SaveButtonDiv = styled.div`
  display: flex;
  margin: 30px auto;
  padding: 0 40px;
  line-height: 1.4;
  border-radius: 16px;
  background-color: ${truewhite};
  white-space: pre-wrap;
  text-align: left;
`

const SaveButton = styled.button`
  color: ${orange};
  background-color: #ff6c0033;
  padding: 10px 40px;
  border: 1px solid ${orange};
  border-radius: 5px;
  margin: 20px 0;
`

const ErrorMessages = styled.div`
  color: ${_formalert};
  margin-top: 8px;
  font-size: 13px;
`

const ErrorMessage = styled.div``

const TitleDiv = styled.div`
  margin: 30px auto;
  padding: 30px 40px;
  border-radius: 16px;
  background-color: ${truewhite};
  text-align: left;

  h1 {
    font-size: 24px;
    font-weight: ${fontWeightBold};
    line-height: 20px;
    white-space: nowrap;
  }
`

const FormPersonaMainContainer = styled.div`
  margin-top: 20px;
  display: flex;
  height: 460px;
  box-shadow: 0px 18px 39px #00000029;
  border-radius: 14px;
  background: ${truewhite};
`

const SaveButtonSideArea = styled.div`
  display: flex;
  justify-content: center;
  ${flexCenter}
  padding: 0 20px;
`

const PersonaCountDiv = styled.div`
  color: ${lightgray};
  font-weight: ${fontWeightBold};
`

const SaveButtonSideAreaInner = styled.div``
